import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const sites = [
  {
    url: "https://g-cut-suleiman.de",
    img: "/portfolio/gcut.jpg",
    name: "G-Cut",
    category: "Beauty · Barbershop",
    location: "Bochum-Wattenscheid",
  },
  {
    url: "https://mda-marketing.netlify.app",
    img: "/portfolio/mda.jpg",
    name: "MDA Studio",
    category: "Agentur · Branding & Design",
    location: "Berlin",
  },
  {
    url: "https://zimmerei-mueller-zeitlofs.netlify.app",
    img: "/portfolio/zimmerei.jpg",
    name: "Zimmerei Müller",
    category: "Handwerk · Zimmerei & Dach",
    location: "Zeitlofs, Bayern",
  },
  {
    url: "https://geffke-fliesen.netlify.app",
    img: "/portfolio/geffke.jpg",
    name: "Geffke Fliesen",
    category: "Handwerk · Fliesenleger",
    location: "Deutschland",
  },
  {
    url: "https://royal-tattoo.netlify.app",
    img: "/portfolio/royal.jpg",
    name: "Royal Tattoo",
    category: "Studio · Tattoo & Piercing",
    location: "Deutschland",
  },
  {
    url: "https://messinger-gutachten.netlify.app",
    img: "/portfolio/messinger.jpg",
    name: "Messinger Gutachten",
    category: "Dienstleistung · KFZ-Gutachter",
    location: "Deutschland",
  },
  {
    url: "https://freischuetz.netlify.app",
    img: "/portfolio/freischuetz.jpg",
    name: "Freischütz",
    category: "Gastronomie · Restaurant",
    location: "Deutschland",
  },
  {
    url: "https://nabil-barbershop.netlify.app",
    img: "/portfolio/nabil.jpg",
    name: "Nabil Barbershop",
    category: "Beauty · Barbershop",
    location: "Deutschland",
  },
];

const stats = [
  { value: "2 Min.", label: "Aufwand pro Website" },
  { value: "1", label: "einziger Prompt" },
  { value: "< 15 Min.", label: "Bis zur fertigen Website" },
  { value: "1.000–3.000€", label: "Verdienst pro Website" },
];

function BrowserMockup({ site, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease, delay }}
      whileHover={{ y: -6, transition: { duration: 0.3, ease } }}
      className="group relative rounded-2xl overflow-hidden"
      style={{ background: "#111111", border: "1px solid rgba(255,255,255,0.08)" }}
      data-testid={`portfolio-card-${site.name}`}
    >
      {/* Browser chrome */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/6" style={{ background: "#1a1a1a" }}>
        <div className="flex gap-1.5 shrink-0">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF5F57" }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#FEBC2E" }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#28C840" }} />
        </div>
        <div
          className="flex-1 rounded-md px-3 py-1 text-center border border-white/5 overflow-hidden"
          style={{ background: "#0e0e0e" }}
        >
          <span className="text-[#86868b] text-xs truncate block">
            {site.url.replace("https://", "")}
          </span>
        </div>
      </div>

      {/* Screenshot preview */}
      <div className="relative overflow-hidden" style={{ height: 220 }}>
        <img
          src={site.img}
          alt={`Website-Vorschau ${site.name}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        {/* Hover overlay */}
        <a
          href={site.url}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute inset-0 flex items-center justify-center transition-all duration-300"
          style={{ background: "rgba(10,10,10,0)" }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(10,10,10,0.35)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "rgba(10,10,10,0)"}
          aria-label={`${site.name} ansehen`}
          data-testid={`portfolio-link-${site.name}`}
        >
          <span
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 font-bold text-sm px-4 py-2 rounded-full"
            style={{ background: "#C9A84C", color: "#0A0A0A" }}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Website ansehen
          </span>
        </a>
      </div>

      {/* Footer */}
      <div className="px-5 py-4">
        <p className="text-[#F5F5F7] font-semibold text-sm">{site.name}</p>
        <p className="text-[#86868b] text-xs mt-0.5">{site.category} · {site.location}</p>
      </div>
    </motion.div>
  );
}

export default function PortfolioSection() {
  return (
    <section
      className="pt-24 md:pt-28 pb-28 md:pb-36 px-6 bg-[#0A0A0A]"
      data-testid="portfolio-section"
      style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease }}
          className="text-center mb-14"
        >
          <span className="text-[#C9A84C] text-xs font-semibold tracking-[0.3em] uppercase">
            Echte Ergebnisse
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#F5F5F7] mt-4 leading-tight tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "-0.02em" }}
          >
            Das entsteht in{" "}
            <span className="text-[#C9A84C]">unter 15 Minuten.</span>
          </h2>
          <p className="text-[#86868b] text-lg mt-4 max-w-2xl mx-auto">
            Komplett von KI generiert — ein Prompt, 2 Minuten Aufwand. Verkaufbar für 1.000–3.000€ pro Website.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease, delay: 0.08 }}
          className="grid grid-cols-2 sm:grid-cols-4 overflow-hidden rounded-2xl mb-14"
          style={{ border: "1px solid rgba(255,255,255,0.06)" }}
        >
          {stats.map((s, i) => (
            <div
              key={i}
              className={`px-6 py-6 text-center bg-[#0A0A0A] ${i < 3 ? "border-r border-white/6" : ""} ${i >= 2 ? "border-t sm:border-t-0 border-white/6" : ""}`}
            >
              <p
                className="text-2xl sm:text-3xl font-black text-[#C9A84C] leading-none"
                style={{ textShadow: "0 0 16px rgba(201,168,76,0.18)" }}
              >
                {s.value}
              </p>
              <p className="text-[#86868b] text-xs mt-2 leading-snug">{s.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Browser mockups */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {sites.map((site, i) => (
            <BrowserMockup key={i} site={site} delay={(i % 2) * 0.1} />
          ))}
          {/* CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease, delay: 0.1 }}
            className="rounded-2xl flex flex-col items-center justify-center text-center p-10 min-h-[280px]"
            style={{ background: "rgba(201,168,76,0.05)", border: "1px dashed rgba(201,168,76,0.35)" }}
            data-testid="portfolio-cta-card"
          >
            <p
              className="text-2xl font-black text-[#F5F5F7] mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Deine nächste Website?
            </p>
            <p className="text-[#86868b] text-sm max-w-xs mb-5">
              Mit derselben Methode baust du sie in unter 15 Minuten — und verkaufst sie für 1.000–3.000€.
            </p>
            <a
              href="https://payhip.com/order?link=8mes1&pricing_plan=YLBN5OxvzK"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#C9A84C] text-[#0A0A0A] font-bold text-sm px-6 py-2.5 rounded-full hover:scale-[1.05] transition-transform duration-300"
              data-testid="portfolio-cta-button"
            >
              Jetzt lernen wie →
            </a>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="text-center text-[#86868b]/60 text-sm mt-8"
        >
          Alle Websites entstanden mit derselben Methode — die du im Kurs lernst.
        </motion.p>
      </div>
    </section>
  );
}
